import React, { useState, useEffect } from 'react';
import ShareModal from '../components/ShareModal';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, onValue, off, getDatabase } from 'firebase/database';
import app from '../config/firebase';
import './GameAnalysis.css';

const database = getDatabase(app);

function GameAnalysis() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [gameStats, setGameStats] = useState({
    players: [],
    totalQuestions: 0,
    totalVotes: 0,
    questionHistory: [],
    badges: []
  });

  useEffect(() => {
    const partyRef = ref(database, `parties/${code}`);
    
    onValue(partyRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setError('Party not found');
        setIsLoading(false);
        return;
      }

      // If game hasn't finished, redirect to appropriate game page
      if (data.gameStarted && !data.gameFinished) {
        // Check game type and redirect accordingly
        if (data.info?.type === 'acordingto') {
          navigate(`/accordingto/${code}`);
        } else {
          navigate(`/game/${code}`);
        }
        return;
      }

      // Check if this is an According To game
      const isAccordingToMode = data.info?.type === 'acordingto';
      
      // Calculate stats based on game type
      let players = [];
      
      if (isAccordingToMode && data.finalScores) {
        // For According To mode, use the finalScores directly
        players = data.finalScores.map(player => ({
          id: player.id,
          name: player.name,
          score: player.score,
          // Include these for compatibility with existing code
          totalVotesReceived: 0,
          mostVotedQuestions: []
        }));
      } else {
        // For regular WIML mode
        players = Object.entries(data.players || {}).map(([id, player]) => ({
          id,
          name: player.name,
          totalVotesReceived: player.totalVotesReceived || 0,
          mostVotedQuestions: player.mostVotedQuestions || []
        }));
      }

      setGameStats({
        players: isAccordingToMode 
          ? players // Already sorted by score in finalScores
          : players.sort((a, b) => b.totalVotesReceived - a.totalVotesReceived),
        totalQuestions: data.totalQuestions || data.questionHistory?.length || 0,
        totalVotes: data.totalVotes || 0,
        questionHistory: data.questionHistory || [],
        badges: data.badges || [],
        isAccordingToMode: isAccordingToMode
      });

      

      setIsLoading(false);
    });

    return () => off(partyRef);
  }, [code, navigate]);

  if (isLoading) {
    return (
      <div className="analysis-container">
        <div className="analysis-content">
          <div className="loading">Loading game analysis...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analysis-container">
        <div className="analysis-content">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="analysis-container">
      <div className="analysis-content">
        <h1>Game Analysis</h1>
        <div className="stats-summary">
          <div className="stat-item">
            <h3>Total Questions</h3>
            <p>{gameStats.totalQuestions}</p>
          </div>
          {!gameStats.isAccordingToMode ? <div className="stat-item">
            <h3>Total Votes</h3>
            <p>{gameStats.totalVotes}</p>
          </div> : null}
        </div>

        <div className="player-rankings">
          <h2>Player Rankings</h2>
          {gameStats.players.map((player, index) => (
            <div key={player.id} className="player-rank">
              <div className="rank-number">#{index + 1}</div>
              <div className="player-info">
                <h3>{player.name}</h3>
                
                {gameStats.isAccordingToMode ? (
                  // According To mode shows scores
                  <p className="score-display">Score: {player.score} points</p>
                ) : (
                  // Regular WIML mode shows votes and badges
                  <>
                    <p>{player.totalVotesReceived} votes received</p>
                    {gameStats.badges[player.id] && (
                      <>
                        <p>{gameStats.badges[player.id].title}</p>
                        <p>{gameStats.badges[player.id].description}</p>
                      </>
                    )}
                  </>
                )}
                
                {!gameStats.isAccordingToMode && gameStats.questionHistory.length > 0 && (
                  <div className="most-voted">
                    <h4>Most voted for:</h4>
                    <ul>
                      {gameStats.questionHistory.map((q, i) => (
                        q.winners?.map((winner, j) => (
                          winner.id === player.id && <li key={`${i}-${j}`}>{q.question} ({winner.votes} votes)</li>
                        ))
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* {gameStats.isAccordingToMode && gameStats.questionHistory.length > 0 && (
                  <div className="according-to-history">
                    <h4>Correct Answers:</h4>
                    <ul>
                      {gameStats.questionHistory.map((q, i) => (
                        q.winners?.some(winner => winner.id === player.id) && 
                        <li key={i}>
                          According to {q.answererName}: {q.question}
                          <span className="correct-indicator">✓</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )} */}
              </div>
            </div>
          ))}
        </div>

        {!gameStats.isAccordingToMode && gameStats.players.map((player) => {
          if (localStorage.getItem('playerId') === player.id) {
            return (
              <React.Fragment key={player.id}>
                <button 
                  className="share-badge-button"
                  onClick={() => setIsModalOpen(true)}
                >
                  Share Your Badge
                </button>
              </React.Fragment>
            );
          }
          return null;
        })}
        

        <button 
          className="new-game-button"
          onClick={() => navigate('/')}
        >
          Start New Game
        </button>
      </div>

      {!gameStats.isAccordingToMode && gameStats.players.map((player) => {
          if (localStorage.getItem('playerId') === player.id && gameStats.badges[player.id]) {
            return (
              <React.Fragment key={player.id}>
                <ShareModal
                  isOpen={isModalOpen}
                  onClose={() => setIsModalOpen(false)}
                  player={{
                    name: player.name,
                    totalVotesReceived: player.totalVotesReceived
                  }}
                  badge={gameStats.badges[player.id]}
                />
              </React.Fragment>
            );
          }
          return null;
        })}
    </div>
  );
}

export default GameAnalysis;
